import {
  Building2,
  Building,
  Briefcase,
  Layers,
  User,
  ShieldCheck,
  ShieldEllipsis,
  FileText,
  Crown,
  Coins,
  PiggyBank,
  BarChart2,
  Star,
  Users,
  Lock,
  Gavel,
  Calculator,
  BookOpen,
  UserCheck,
  ClipboardList,
  Zap,
  Pen,
  type LucideIcon,
} from 'lucide-react';
import type { SuggestHitIconKind } from '@/gql/graphql';

/**
 * Maps the SuggestHitIconKind enum (from BE handover §1) to lucide icons.
 * Design §6.4 extended to cover all 34 handover enum values.
 * Unknown tokens fall back to `Layers` (generic entity icon).
 * APPOINTMENT_TRUSTEE uses Gavel (Scale is not in standard lucide exports).
 */
const ICON_MAP: Partial<Record<SuggestHitIconKind, LucideIcon>> = {
  // Entity subtypes
  ENTITY_GENERIC: Building2,
  ENTITY_LIMITED: Building2,
  ENTITY_PLC: Building2,
  ENTITY_LLP: Building,
  ENTITY_PARTNERSHIP: Building,
  ENTITY_SOLE_TRADER: User,
  ENTITY_TRUST: Briefcase,
  ENTITY_FOUNDATION: Briefcase,
  ENTITY_BRANCH: Building2,
  ENTITY_HOLDING: Building2,
  ENTITY_COOPERATIVE: Users,
  ENTITY_NGO: Star,
  ENTITY_CHARITY: Star,
  // Person
  PERSON: User,
  // Appointment subtypes
  APPOINTMENT_DIRECTOR: ShieldCheck,
  APPOINTMENT_SECRETARY: FileText,
  APPOINTMENT_OFFICER: ShieldEllipsis,
  APPOINTMENT_CHAIRMAN: Crown,
  APPOINTMENT_CEO: Crown,
  APPOINTMENT_CFO: Calculator,
  APPOINTMENT_COO: ClipboardList,
  APPOINTMENT_CTO: Zap,
  APPOINTMENT_TREASURER: Coins,
  APPOINTMENT_AUDITOR: BookOpen,
  APPOINTMENT_UBO: Lock,
  APPOINTMENT_SHAREHOLDER: BarChart2,
  APPOINTMENT_NOMINEE: UserCheck,
  APPOINTMENT_TRUSTEE: Gavel, // Scale not in standard lucide exports — Gavel is the closest
  APPOINTMENT_PROTECTOR: Gavel,
  APPOINTMENT_SIGNATORY: Pen,
  APPOINTMENT_GENERIC: FileText,
  // Financial kinds
  SHARE_CLASS: Coins,
  SHARE_INTEREST: PiggyBank,
  OWNERSHIP: Crown,
};

interface SuggestHitIconProps {
  iconKind: SuggestHitIconKind;
  size?: number;
  className?: string;
}

/**
 * Renders the lucide icon for a given SuggestHitIconKind.
 * Falls back to `Layers` for any unmapped token (future-proof against new BE enum values).
 */
export function SuggestHitIcon({
  iconKind,
  size = 14,
  className,
}: SuggestHitIconProps) {
  const Icon = ICON_MAP[iconKind] ?? Layers;
  return <Icon size={size} className={className} aria-hidden />;
}
